import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    IconButton,
    Grid,
    Button,
    Divider,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { isEqual, cloneDeep } from 'lodash';
import InformationSection from './InformationSection';
import ContentSection from './ContentSection';
import './NotificationTemplatesEdit.scss';

/**
 * @param {{
 *  open: boolean;
 *  setOpen: Function;
 *  mode: 'create' | 'edit' | 'duplicate';
 *  dataSet: any;
 *  onSave: Function;
 * }} props
 */
function NotificationTemplatesEdit({ open, setOpen, mode, dataSet, onSave }) {
    const { t } = useTranslation('notificationTemplates');
    const informationSectionRef = useRef(null);
    const ContentSectionRef = useRef(null);

    const [formData, setFormData] = useState({
        id: null,
        name: '',
        description: '',
        templateSubject: '',
        templateContent: '',
        isFile: false,
        isAvailableForUse: false,
        type: 'Email',
        languageCode: 'en-us',
        versionName: 'v1',
    });

    const [originalData, setOriginalData] = useState({});
    const [validationErrors, setValidationErrors] = useState({
        name: false,
        templateSubject: false,
        templateContent: false,
        nameErrorMessage: '',
        templateSubjectErrorMessage: '',
        templateContentErrorMessage: '',
    });

    useEffect(() => {
        if (open && dataSet) {
            if (mode === 'edit') {
                const editData = {
                    ...cloneDeep(dataSet),
                    versionName: dataSet.versionName || 'v1',
                };
                setFormData(editData);
                setOriginalData(editData);
            } else if (mode === 'duplicate') {
                const duplicatedData = {
                    ...cloneDeep(dataSet),
                    id: null,
                    name: `Copy of ${dataSet.name}`,
                    versionName: 'v1', // Reset version for duplicates
                };
                setFormData(duplicatedData);
                setOriginalData(duplicatedData);
            }
        } else if (open && mode === 'create') {
            const emptyData = {
                id: null,
                name: '',
                description: '',
                templateSubject: '',
                templateContent: '',
                isFile: false,
                isAvailableForUse: false,
                type: 'Email',
                languageCode: 'en-us',
                versionName: 'v1',
            };
            setFormData(emptyData);
            setOriginalData(emptyData);
        }
    }, [open, dataSet, mode]);

    useEffect(() => {
        setValidationErrors({
            name: false,
            templateSubject: false,
            templateContent: false,
            nameErrorMessage: '',
            templateSubjectErrorMessage: '',
            templateContentErrorMessage: '',
        });
    }, [JSON.stringify(formData)]);

    const validate = () => {
        let hasError = false;
        let errorRef;
        const errors = {
            name: false,
            templateSubject: false,
            templateContent: false,
            nameErrorMessage: '',
            templateSubjectErrorMessage: '',
            templateContentErrorMessage: '',
        };

        // Validate name
        if (!formData.name.trim()) {
            hasError = true;
            errors.name = true;
            errors.nameErrorMessage = t('validation.nameRequired');
            errorRef = informationSectionRef;
        } else if (formData.name.length > 50) {
            hasError = true;
            errors.name = true;
            errors.nameErrorMessage = t('validation.nameTooLong');
            errorRef = informationSectionRef;
        }

        // Validate template subject
        if (!formData.templateSubject.trim()) {
            hasError = true;
            errors.templateSubject = true;
            errors.templateSubjectErrorMessage = t('validation.templateSubjectRequired');
            errorRef = errorRef || ContentSectionRef;
        } else if (formData.templateSubject.length > 50) {
            hasError = true;
            errors.templateSubject = true;
            errors.templateSubjectErrorMessage = t('validation.templateSubjectTooLong');
            errorRef = errorRef || ContentSectionRef;
        }

        // Validate template content
        if (!formData.templateContent.trim()) {
            hasError = true;
            errors.templateContent = true;
            errors.templateContentErrorMessage = t('validation.templateContentRequired');
            errorRef = errorRef || ContentSectionRef;
        }

        setValidationErrors(errors);

        if (hasError && errorRef?.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return !hasError;
    };

    const closeSidePanel = () => {
        setOpen(false);
        setFormData({
            id: null,
            name: '',
            description: '',
            templateSubject: '',
            templateContent: '',
            isFile: false,
            isAvailableForUse: false,
            type: 'Email',
            languageCode: 'en-us',
            versionName: 'v1',
        });
        setValidationErrors({
            name: false,
            templateSubject: false,
            templateContent: false,
            nameErrorMessage: '',
            templateSubjectErrorMessage: '',
            templateContentErrorMessage: '',
        });
    };

    const handleSubmit = async () => {
        if (!validate()) {
            return;
        }
        // Generate new version name for edits
        const generateNewVersionName = (currentVersion) => {
            if (!currentVersion || currentVersion === 'v1') return 'v2';
            const match = currentVersion.match(/^v(\d+)$/);
            if (match) {
                const nextVersion = parseInt(match[1], 10) + 1;
                return `v${nextVersion}`;
            }
            return `${currentVersion}_v2`;
        };

        const dataToSave = {
            name: formData.name,
            description: formData.description,
            templateSubject: formData.templateSubject,
            templateContent: formData.templateContent,
            isFile: formData.isFile,
            isAvailableForUse: formData.isAvailableForUse,
            type: formData.type,
            languageCode: formData.languageCode,
            versionName: mode === 'edit' ? generateNewVersionName(formData.versionName) : (formData.versionName || 'v1'),
        };

        // For edit mode, include the ID
        if (mode === 'edit') {
            dataToSave.id = formData.id;
        }

        // For duplicate mode, include the source ID
        if (mode === 'duplicate') {
            dataToSave.sourceId = dataSet.id;
        }

        try {
            await onSave(dataToSave, mode);
            // Success message is handled in the parent component
        } catch (error) {
            // exception not throw
        }
    };

    const updateFormData = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isUnchanged = isEqual(originalData, formData) && mode !== 'duplicate';
    let stepIndex = 1;

    const getTitle = () => {
        if (mode === 'create') return t('sidePanel.createTitle');
        if (mode === 'edit') return t('sidePanel.editTitle');
        if (mode === 'duplicate') return t('sidePanel.duplicateTitle');
        return t('sidePanel.title');
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            classes={{ paper: 'NotificationTemplatesEditDrawerPaper' }}
        >
            <Grid
                container
                justifyContent="space-between"
                className="dse-header-section"
            >
                <Grid
                    item
                    className="title-container"
                >
                    <h3 className="title-label">
                        {getTitle()}
                    </h3>
                </Grid>
                <Grid item>
                    <IconButton
                        className="drawer-x-icon-button"
                        onClick={() => closeSidePanel()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            size="sm"
                        />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid
                container
                className="dse-content-section"
            >
                <InformationSection
                    stepIndex={stepIndex++}
                    errorRef={informationSectionRef}
                    validationErrors={validationErrors}
                    formData={formData}
                    updateFormData={updateFormData}
                />
                <ContentSection
                    stepIndex={stepIndex++}
                    errorRef={ContentSectionRef}
                    validationErrors={validationErrors}
                    formData={formData}
                    updateFormData={updateFormData}
                />
            </Grid>
            <Divider />
            <Grid
                container
                justifyContent="flex-end"
                className="dse-footer-section"
            >
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isUnchanged}
                >
                    {mode === 'create' ? t('actions.create') : t('actions.save')}
                </Button>
                <Button
                    variant="contained"
                    onClick={closeSidePanel}
                >
                    {t('actions.cancel')}
                </Button>
            </Grid>
        </Drawer>
    );
}

export default NotificationTemplatesEdit;

